import { Fragment, useState, type ReactNode } from 'react';
import type { LexiconMeaning, LexiconWord } from '../../lib/lexiconApi.js';
import type { MeaningLookup, WordLookupEntry } from './types.js';
import { normalizeWord } from './types.js';

/**
 * El cuerpo del popover con el «significado de esa frase» (encargo 49), en la
 * forma «Con ejemplo» que eligió Emmanuel (encargo 64 del tablero de mejoras):
 * la traducción del significado, «También:» con sus otras traducciones, la
 * definición, el ejemplo del diccionario traducido y los otros significados de
 * la palabra como chips — tocar uno cambia la tarjeta, sin pedido nuevo (todo
 * viene en la entrada que ya trajo `POST /lexicon/meaning`).
 *
 * Lo que sale del diccionario lo dice; lo que no, también: nada generado se
 * presenta como un significado del diccionario.
 */

/** Cuántos chips se ven antes del «+N» (como en la maqueta: cuatro y «+3»). */
export const CHIPS_VISIBLE = 4;

/**
 * Qué significado muestra la tarjeta. El elegido por el selector, salvo que el
 * alumno toque otro chip; sin elegido (`unchosen`), ninguno hasta que toque.
 * `null` fuera de los dos casos con entrada del diccionario.
 */
export function shownMeaning(
  meaning: MeaningLookup,
  chosenId: string | null,
): LexiconMeaning | null {
  if (meaning.kind !== 'picked' && meaning.kind !== 'unchosen') return null;
  const id = chosenId ?? (meaning.kind === 'picked' ? meaning.meaningId : null);
  if (id === null) return null;
  const m = meaning.entry.meanings?.find((x) => x.id === id);
  if (!m) throw new Error(`word-pop: ${id} no está en la entrada ${meaning.entry.text}`);
  return m;
}

/** La pronunciación escrita, entre barras (el diccionario las trae con y sin). */
export function ipaLabel(ipa: string): string {
  const t = ipa.trim();
  return t.startsWith('/') || t.startsWith('[') ? t : `/${t}/`;
}

const comparable = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9à-ÿ']+/gi, ' ')
    .trim();

/**
 * ¿El ejemplo del diccionario es la frase de la charla (o está entero en ella)?
 * Pasa: el coach usa los ejemplos del diccionario. Entonces repetirlo no suma —
 * lo que suma es su traducción, que es la de la frase que el alumno está leyendo.
 */
export function exampleIsSentence(example: string, sentence: string | undefined): boolean {
  if (!sentence) return false;
  const ex = comparable(example);
  return ex !== '' && ` ${comparable(sentence)} `.includes(` ${ex} `);
}

/** Las formas de la palabra que se resaltan en el ejemplo: la tocada, la entrada y su conjugación o plural. */
function formsOf(word: string, entry: LexiconWord | null): Set<string> {
  const forms = new Set([normalizeWord(word)]);
  if (entry) {
    const lemma = normalizeWord(entry.text);
    forms.add(lemma);
    forms.add(`${lemma}s`);
    forms.add(`${lemma}es`);
    const v = entry.verbForms;
    if (v)
      for (const f of [v.base, v.thirdPerson, v.past, v.pastParticiple, v.gerund])
        forms.add(normalizeWord(f));
  }
  forms.delete('');
  return forms;
}

/** Se resalta la palabra sola: la puntuación pegada («open,») queda afuera, como en `normalizeWord`. */
function highlighted(text: string, forms: Set<string>): ReactNode[] {
  return text.split(/(\s+)/).map((part, i) => {
    if (!forms.has(normalizeWord(part))) return part;
    const [, lead = '', word = '', trail = ''] =
      /^([^a-zà-ÿ]*)(.*?)([^a-zà-ÿ']*)$/is.exec(part) ?? [];
    return (
      <Fragment key={i}>
        {lead}
        <mark className="word-pop-ex-hl">{word}</mark>
        {trail}
      </Fragment>
    );
  });
}

/** El ejemplo y su traducción; si el ejemplo es la frase de la charla, sólo la traducción («En esta frase»). */
function ExampleBlock({
  example,
  exampleEs,
  sentence,
  forms,
  clamp,
}: {
  example: string;
  exampleEs: string;
  sentence: string | undefined;
  forms: Set<string>;
  clamp: string;
}) {
  if (exampleIsSentence(example, sentence)) {
    if (!exampleEs) return null;
    return (
      <div className="word-pop-exblock" data-example="sentence">
        <div className="word-pop-lab">En esta frase</div>
        <p className={`word-pop-exes is-main${clamp}`}>{exampleEs}</p>
      </div>
    );
  }
  if (!example) return null;
  return (
    <div className="word-pop-exblock" data-example="dictionary">
      <div className="word-pop-lab">Ejemplo</div>
      <p className={`word-pop-exen${clamp}`}>{highlighted(example, forms)}</p>
      {exampleEs && <p className={`word-pop-exes${clamp}`}>{exampleEs}</p>}
    </div>
  );
}

/** Un significado entero: traducción, «También:», definición, de dónde sale y ejemplo. */
function MeaningCard({
  tr,
  alts,
  definition,
  example,
  exampleEs,
  source,
  sentence,
  forms,
}: {
  tr: string;
  alts: ReadonlyArray<string>;
  definition: string | null;
  example: string;
  exampleEs: string;
  source: string;
  sentence: string | undefined;
  forms: Set<string>;
}) {
  // Los textos largos (el 10 % de los ejemplos pasa las cuatro líneas) se cortan
  // con «…»; tocar la tarjeta los muestra enteros.
  const [full, setFull] = useState(false);
  const clamp = full ? '' : ' is-clamped';
  return (
    <div className="word-pop-card" onClick={() => setFull(true)}>
      <div className="word-pop-es">
        <span className="word-pop-es-flag">ES</span>
        <span>{tr}</span>
      </div>
      {alts.length > 0 && (
        <div className="word-pop-alts">
          También:{' '}
          {alts.map((a, i) => (
            <span key={a}>
              {i > 0 && ', '}
              <b>{a}</b>
            </span>
          ))}
        </div>
      )}
      {definition && <div className={`word-pop-lit${clamp}`}>{definition}</div>}
      <div className="word-pop-src">{source}</div>
      <ExampleBlock
        example={example}
        exampleEs={exampleEs}
        sentence={sentence}
        forms={forms}
        clamp={clamp}
      />
    </div>
  );
}

/**
 * Los significados de la palabra como chips, con el que se muestra marcado.
 * Hasta `CHIPS_VISIBLE`; el resto detrás de «+N» (tocarlo los muestra todos).
 * Con el elegido por el selector primero, para que se vea aunque sea el quinto.
 */
function MeaningChips({
  entry,
  first,
  shownId,
  label,
  onChoose,
}: {
  entry: LexiconWord;
  first: string | null;
  shownId: string | null;
  label: string;
  onChoose: (id: string) => void;
}) {
  const [all, setAll] = useState(false);
  const meanings = entry.meanings ?? [];
  const ordered = first
    ? [...meanings.filter((m) => m.id === first), ...meanings.filter((m) => m.id !== first)]
    : meanings;
  // Un «+1» ocupa lo mismo que el chip que esconde: con uno de más se ven todos.
  const visible =
    all || ordered.length <= CHIPS_VISIBLE + 1 ? ordered : ordered.slice(0, CHIPS_VISIBLE);
  const hidden = ordered.length - visible.length;
  // Dos significados con la misma traducción se distinguen por su tipo.
  const repeated = (m: LexiconMeaning) => meanings.filter((x) => x.tr === m.tr).length > 1;
  return (
    <div className="word-pop-others">
      <div className="word-pop-lab">{label}</div>
      <div className="word-pop-chips">
        {visible.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`word-pop-chip ${m.id === shownId ? 'is-on' : ''}`}
            aria-pressed={m.id === shownId}
            onClick={() => onChoose(m.id)}
          >
            {repeated(m) ? `${m.tr} (${m.pos})` : m.tr}
          </button>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            className="word-pop-chip more"
            aria-label={`Ver ${hidden} significados más`}
            onClick={() => setAll(true)}
          >
            +{hidden}
          </button>
        )}
      </div>
    </div>
  );
}

/** Lo que dice el aviso de un faltante, según quedó en el registro. */
function missingLine(
  missing: 'pending' | 'not-registered' | null,
  what: 'meaning' | 'word',
): string | null {
  if (missing === 'pending')
    return what === 'meaning'
      ? 'Puede que falte este significado en el diccionario: lo estamos revisando.'
      : 'Falta en el diccionario: lo estamos revisando.';
  if (missing === 'not-registered' && what === 'word') return 'No está en el diccionario.';
  return null;
}

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function MeaningBody({
  entry,
  meaning,
  word,
  sentence,
  shown,
  onChoose,
}: {
  entry: WordLookupEntry;
  meaning: MeaningLookup;
  word: string;
  /** La frase de la charla donde se tocó la palabra. */
  sentence: string | undefined;
  /** El significado que se muestra (`shownMeaning`). */
  shown: LexiconMeaning | null;
  onChoose: (id: string) => void;
}) {
  switch (meaning.kind) {
    case 'picked': {
      if (!shown) throw new Error(`word-pop: el elegido ${meaning.meaningId} no se encontró`);
      const forms = formsOf(word, meaning.entry);
      const own = shown.id === meaning.meaningId;
      return (
        <div className="word-pop-meaning" data-meaning="picked">
          <MeaningCard
            key={shown.id}
            tr={shown.tr}
            alts={shown.alts}
            definition={shown.definition}
            example={shown.example}
            exampleEs={shown.exampleEs}
            source={
              own
                ? 'Significado en esta frase · del diccionario'
                : 'Otro significado · del diccionario'
            }
            // El ejemplo sólo puede ser «esta frase» en el significado de esta frase.
            sentence={own ? sentence : undefined}
            forms={forms}
          />
          {(meaning.entry.meanings?.length ?? 0) > 1 && (
            <MeaningChips
              key={meaning.entry.id}
              entry={meaning.entry}
              first={meaning.meaningId}
              shownId={shown.id}
              label={`«${capitalized(meaning.entry.text)}» también puede ser`}
              onChoose={onChoose}
            />
          )}
        </div>
      );
    }
    case 'unchosen': {
      const note = missingLine(meaning.reason === 'none' ? meaning.missing : null, 'meaning');
      return (
        <div className="word-pop-meaning" data-meaning="unchosen">
          <div className="word-pop-empty">
            No pudimos elegir el significado de «{word}» en esta frase.
          </div>
          {note && <div className="word-pop-src">{note}</div>}
          {shown && (
            <MeaningCard
              key={shown.id}
              tr={shown.tr}
              alts={shown.alts}
              definition={shown.definition}
              example={shown.example}
              exampleEs={shown.exampleEs}
              source="Uno de sus significados · del diccionario"
              sentence={undefined}
              forms={formsOf(word, meaning.entry)}
            />
          )}
          {(meaning.entry.meanings?.length ?? 0) > 0 && (
            <MeaningChips
              key={meaning.entry.id}
              entry={meaning.entry}
              first={null}
              shownId={shown?.id ?? null}
              label={`«${capitalized(meaning.entry.text)}» puede ser`}
              onChoose={onChoose}
            />
          )}
        </div>
      );
    }
    case 'missing-word': {
      const note = missingLine(meaning.missing, 'word');
      return (
        <div className="word-pop-meaning" data-meaning="missing">
          {meaning.autoTranslation && entry.translation && (
            <>
              <div className="word-pop-es">
                <span className="word-pop-es-flag">ES</span>
                <span>{entry.translation}</span>
              </div>
              <div className="word-pop-src">
                Traducción automática de esta frase · no es del diccionario
              </div>
            </>
          )}
          {note && <div className="word-pop-empty">{note}</div>}
        </div>
      );
    }
    case 'provisional':
      return (
        <div className="word-pop-meaning" data-meaning="provisional">
          <MeaningCard
            tr={entry.translation}
            alts={[]}
            definition={entry.definition}
            example={meaning.draft.example}
            exampleEs={meaning.draft.exampleEs}
            source="Significado provisorio · lo revisó una persona; todavía no está en el diccionario"
            sentence={sentence}
            forms={formsOf(word, null)}
          />
        </div>
      );
    case 'not-english':
      return (
        <div className="word-pop-meaning" data-meaning="not-english">
          <div className="word-pop-empty">
            «{meaning.word}» está en español: esta consulta busca palabras en inglés.
          </div>
        </div>
      );
  }
}
